import {
  Flex,
  Text,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { DeliverableContainerGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  calculateDetailedStatus,
  DetailedStatus,
  DetailedStatusCell,
} from '../Status';
import {
  getDeliverableDetailViewUrl,
  getDeliverableFinalOutputReceiptsViewUrl,
} from '../utils';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { useCreateDeliverableMutation } from '~/core/routes/deliverables/mutations/CreateDeliverableByDueDate.gql.generated';
import {
  GetDeliverableContainersDocumentGQLType,
  useGetDeliverableContainersQuery,
} from '~/core/routes/deliverables/queries/GetDeliverableContainers.gql.generated';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';

const getNextStepsContainers = (containers?: DeliverableContainerGQLType[]) => {
  const finishUploading = [];
  const overdue = [];
  const remediationRequested = [];
  const readyToGenerateReport = [];
  const readyToSubmit = [];
  let size = 0;

  if (containers) {
    for (const container of containers) {
      const status = calculateDetailedStatus(container);

      switch (status) {
        case 'finishUploading':
          finishUploading.push(container);
          size += 1;
          break;
        case 'readyToGenerateReport':
          readyToGenerateReport.push(container);
          size += 1;
          break;
        case 'overdue':
          overdue.push(container);
          size += 1;
          break;
        case 'requestRemediation':
          remediationRequested.push(container);
          size += 1;
          break;
        case 'readyToSubmit':
          readyToSubmit.push(container);
          size += 1;
          break;
        default:
          break;
      }
    }
  }

  return {
    finishUploading,
    overdue,
    remediationRequested,
    readyToGenerateReport,
    readyToSubmit,
    size,
  };
};

const TaskCenterButton = ({
  container,
  status,
}: {
  container: DeliverableContainerGQLType;
  status: DetailedStatus;
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [createDeliverableMutation] = useCreateDeliverableMutation({
    onError: () => {
      toast({
        title:
          'Unable to create this deliverable container. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    },
    // !TODO @sarah-finley Does this need refetch variables?
    refetchQueries: [GetDeliverableContainersDocumentGQLType],
  });

  const onClick = useCallback(async () => {
    switch (status) {
      case 'overdue':
        // if deliverable doesnt exist, create it here, otherwise navigate to the existing one
        if (
          container.totalDependencies === -1 &&
          container.totalFinalArtifacts === -1
        ) {
          const { data: newDeliverableData } = await createDeliverableMutation({
            variables: {
              creditFacilityId: container.creditFacilityId,
              dueDate: container.dueDate,
              configKey: container.configKey,
            },
          });

          finleyAssert(
            newDeliverableData?.deliverable,
            'Failed to manually create a deliverable from task center.',
          );
          const id = newDeliverableData.deliverable.id;
          navigate(getDeliverableDetailViewUrl(id));
        } else {
          navigate(getDeliverableDetailViewUrl(container.id));
        }
        break;
      case 'finishUploading':
      case 'readyToGenerateReport':
        navigate(getDeliverableDetailViewUrl(container.id));
        break;
      case 'requestRemediation':
      case 'readyToSubmit':
        finleyAssert(
          container.latestReceiptId,
          `Cannot have status: ${status} without a latestReceiptId`,
        );
        navigate(
          getDeliverableFinalOutputReceiptsViewUrl({
            containerId: container.id,
            receiptId: container.latestReceiptId,
          }),
        );
        break;
      default:
        break;
    }
  }, [
    container.configKey,
    container.creditFacilityId,
    container.dueDate,
    container.id,
    container.latestReceiptId,
    container.totalDependencies,
    container.totalFinalArtifacts,
    createDeliverableMutation,
    navigate,
    status,
  ]);

  return (
    <Button
      whiteSpace="normal"
      textAlign="left"
      px={4}
      py={8}
      fontWeight="normal"
      fontSize="sm"
      rightIcon={<Icon icon="chevron-right" color="blue.300" />}
      onClick={onClick}
    >
      {`${container.creditFacilityName} \n ${commonUtils.toTitleCase(
        container.configKey,
      )} - ${commonDate.fullDateStrToHumanDateString(container.dueDate)}`}
    </Button>
  );
};

const TaskCenterByStatus = ({
  containersWithStatus,
}: {
  containersWithStatus: DeliverableContainerGQLType[];
}) => {
  if (containersWithStatus.length === 0) {
    return <></>;
  }

  const status = calculateDetailedStatus(containersWithStatus[0]);
  return (
    <>
      <DetailedStatusCell status={status} />
      {containersWithStatus.map((container) => {
        return (
          <TaskCenterButton
            key={`${container.id}-${status}`}
            container={container}
            status={status}
          />
        );
      })}
    </>
  );
};

const TaskCenterDrawerBase = () => {
  const taskDrawerControls = useDisclosure();

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );
  const deliverableContainersQuery = useGetDeliverableContainersQuery({
    variables: { creditFacilityIds, limit: 100 },
  });
  if (deliverableContainersQuery.error) throw deliverableContainersQuery.error;

  const nextStepContainers = getNextStepsContainers(
    deliverableContainersQuery.data?.getDeliverableContainers,
  );
  const hasPendingTasks = nextStepContainers.size > 0;
  return (
    <>
      {/* Trigger */}
      <IconButtonV2
        isLoading={deliverableContainersQuery.loading}
        variant="primary-muted"
        placement="bottom"
        aria-label={hasPendingTasks ? 'Pending Tasks' : 'Tasks'}
        hasDotIndicator={hasPendingTasks}
        icon={<Icon icon="list-check" />}
        isActive={taskDrawerControls.isOpen}
        onClick={taskDrawerControls.onToggle}
      />

      {/* Content */}
      <Drawer {...taskDrawerControls} placement="right" closeOnOverlayClick>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Tasks</DrawerHeader>
          <DrawerBody>
            {(() => {
              if (!hasPendingTasks) {
                return <Text>Currently, there are no active tasks.</Text>;
              }

              return (
                <Flex direction="column" gap="1.5rem">
                  <TaskCenterByStatus
                    containersWithStatus={nextStepContainers.overdue}
                  />
                  <TaskCenterByStatus
                    containersWithStatus={
                      nextStepContainers.remediationRequested
                    }
                  />
                  <TaskCenterByStatus
                    containersWithStatus={nextStepContainers.readyToSubmit}
                  />
                  <TaskCenterByStatus
                    containersWithStatus={
                      nextStepContainers.readyToGenerateReport
                    }
                  />
                  <TaskCenterByStatus
                    containersWithStatus={nextStepContainers.finishUploading}
                  />
                </Flex>
              );
            })()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export const TaskCenterDrawer = () => (
  <ErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return (
        // Disabled icon button as fallback UI
        <IconButtonV2
          isDisabled
          variant="primary-muted"
          placement="bottom"
          aria-label="Tasks"
          icon={<Icon icon="list-check" />}
        />
      );
    }}
  >
    <TaskCenterDrawerBase />
  </ErrorBoundary>
);
